import { FormEvent, useState } from 'react';
import { dealerErrorCode, dealerErrorFields, dealerErrorMessage, dealersApi, isFeatureOff } from '../../services/dealersApi';
import { DealerUser, DealerUserRole, DealerUserStatus } from '../../types/dealers';
import { describedBy, FormField, RadioGroup } from '../promotions/PromoFormParts';
import UsernameAvailability from '../UsernameAvailability';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^\+?[\d\s-]{8,20}$/;

interface DealerUserFormProps {
  dealerId: string;
  /** null = add a user (provisions the account in identity); otherwise edit role, spend flag, status. */
  user: DealerUser | null;
  token: string;
  onClose: () => void;
  onSaved: (saved: DealerUser) => void;
  onFeatureOff: () => void;
}

/**
 * Spec 085 Flow A step 3 — add or edit a dealer user. The dealer signs in with the username (BR-2,
 * garage-partner rules, live availability check). Username, name, phone and email are fixed once the
 * account exists (identity owns them). Phone and email only have to be unique among dealer users, so
 * they may already be on the person's customer account; 409 `IDENTITY_CONFLICT` (another dealer user)
 * lands on the phone and email fields, `USERNAME_TAKEN` / `USERNAME_INVALID` on the username.
 */
export default function DealerUserForm({ dealerId, user, token, onClose, onSaved, onFeatureOff }: DealerUserFormProps) {
  const editing = user !== null;
  const [name, setName] = useState(user?.name ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [username, setUsername] = useState(user?.username ?? '');
  const [usernameValid, setUsernameValid] = useState(false);
  const [role, setRole] = useState<DealerUserRole>(user?.role ?? 'STAFF');
  const [canCommitSpend, setCanCommitSpend] = useState(user?.canCommitSpend ?? false);
  const [status, setStatus] = useState<DealerUserStatus>(user?.status ?? 'ACTIVE');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setFormError(null);
    const local: Record<string, string> = {};
    if (!editing) {
      if (!usernameValid) local.username = 'Choose a valid, available username';
      if (!name.trim()) local.name = 'Enter the name';
      if (!PHONE.test(phone.trim())) local.phone = 'Enter a valid mobile number, e.g. +971501234567';
      if (!EMAIL.test(email.trim())) local.email = 'Enter a valid email';
    }
    if (Object.keys(local).length) { setErrors(local); return; }
    setErrors({});
    if (user && status === 'DISABLED' && user.status !== 'DISABLED'
      && !window.confirm(`Disable ${user.name}? This also blocks their dealer portal login, and a disabled user cannot be re-enabled (add a new user instead).`)) return;
    setSaving(true);
    try {
      const saved = user
        ? await dealersApi.updateUser(dealerId, user.id, {
          ...(canCommitSpend !== user.canCommitSpend ? { canCommitSpend } : {}),
          ...(status !== user.status ? { status } : {}),
        }, token)
        : await dealersApi.createUser(dealerId, { name: name.trim(), phone: phone.trim(), email: email.trim(), username: username.trim().toLowerCase(), role, canCommitSpend }, token);
      onSaved(saved);
    } catch (err) {
      if (isFeatureOff(err)) { onFeatureOff(); onClose(); return; }
      if (dealerErrorCode(err) === 'IDENTITY_CONFLICT') {
        const msg = dealerErrorMessage(err);
        setErrors({ phone: msg, email: msg });
        return;
      }
      const fields = dealerErrorFields(err);
      if (Object.keys(fields).length) setErrors(fields);
      setFormError(dealerErrorMessage(err, 'Failed to save the user'));
    } finally {
      setSaving(false);
    }
  };

  const owner = role === 'OWNER';

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal" role="dialog" aria-modal="true" aria-label={editing ? `Edit ${user.name}` : 'Add dealer user'} style={{ maxWidth: '520px', width: '100%' }}>
        <div className="admin-modal-header">
          <h3 style={{ margin: 0 }}>{editing ? `Edit ${user.name}` : 'Add dealer user'}</h3>
          <button type="button" className="admin-modal-close" onClick={onClose} disabled={saving} aria-label="Close">&times;</button>
        </div>
        <form onSubmit={submit} noValidate>
          <div className="admin-modal-body">
            {formError && <div className="admin-info-banner danger" role="alert" data-testid="dealer-user-form-error">{formError}</div>}
            <FormField id="dealer-user-username" label="Username" error={errors.username}
              hint={editing
                ? (user.username ? undefined : 'Created before usernames: this user signs in with the email.')
                : 'The dealer signs in to the dealer portal with this username.'}>
              <input id="dealer-user-username" className="admin-form-input" value={editing ? (user.username ?? user.email) : username}
                maxLength={30} disabled={saving || editing} dir="ltr" spellCheck={false} autoComplete="off" placeholder="e.g. sara.haddad"
                {...describedBy('dealer-user-username', errors.username, !editing || !user.username)}
                onChange={(e) => setUsername(e.target.value.toLowerCase())} />
              {!editing && <UsernameAvailability value={username} onValidityChange={setUsernameValid} />}
            </FormField>
            <FormField id="dealer-user-name" label="Name" error={errors.name}>
              <input id="dealer-user-name" className="admin-form-input" value={name} maxLength={120} disabled={saving || editing}
                {...describedBy('dealer-user-name', errors.name)} onChange={(e) => setName(e.target.value)} />
            </FormField>
            <FormField id="dealer-user-phone" label="Phone" error={errors.phone}>
              <input id="dealer-user-phone" className="admin-form-input" type="tel" value={phone} maxLength={32} disabled={saving || editing}
                {...describedBy('dealer-user-phone', errors.phone)} onChange={(e) => setPhone(e.target.value)} />
            </FormField>
            <FormField id="dealer-user-email" label="Email" error={errors.email}
              hint={editing ? undefined : 'The welcome email with the dealer portal link and set-password step goes here. Phone and email may already be on this person\'s customer account; they only have to be unique among dealer users.'}>
              <input id="dealer-user-email" className="admin-form-input" type="email" value={email} maxLength={254} disabled={saving || editing}
                {...describedBy('dealer-user-email', errors.email, !editing)} onChange={(e) => setEmail(e.target.value)} />
            </FormField>
            <RadioGroup name="dealer-user-role" label="Role" value={role} disabled={saving || editing} onChange={setRole}
              options={[{ value: 'OWNER', label: 'Owner' }, { value: 'STAFF', label: 'Staff' }]} />
            {editing && <div className="dealer-muted">The role is fixed once the account exists. To change it, disable this user and add a new one.</div>}
            <label className="dealer-checkbox">
              <input type="checkbox" checked={owner || canCommitSpend} disabled={saving || owner} onChange={(e) => setCanCommitSpend(e.target.checked)} />
              Can commit spend (accept proformas, buy packs)
            </label>
            {owner && <div className="dealer-muted">Owners can always commit spend.</div>}
            {editing && user.status === 'DISABLED' && (
              <div className="dealer-muted" data-testid="dealer-user-disabled-note">Account disabled. A disabled user cannot be re-enabled; add a new user instead.</div>
            )}
            {editing && user.status !== 'DISABLED' && (
              <RadioGroup name="dealer-user-status" label="Account" value={status} disabled={saving} onChange={setStatus}
                options={[{ value: 'ACTIVE', label: 'Active' }, { value: 'DISABLED', label: 'Disabled (also blocks the login, cannot be undone)' }]} />
            )}
          </div>
          <div className="admin-modal-footer">
            <button type="button" className="admin-btn admin-btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Add user'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
